import ReportType from 'model/reports/ReportType';
import QuestionReports from 'model/reports/QuestionResults';
import { ReactElement } from 'react';
import { BarChartReport } from 'components/reports/BarChartReport';
import { HorizontalBarChartReport } from 'components/reports/HorizontalBarChartReport';
import { ListResults } from 'model/reports/ListResults';
import React from 'react';
import { PieChartReport } from 'components/reports/PieChartReport';
import { DoughnutChartReport } from 'components/reports/DoughnutChartReport';
import { StackedBarChartReport } from 'components/reports/StackedBarChartReport';
import { GridResults } from 'model/reports/GridResults';
import { HorizontalStackedBarChartReport } from 'components/reports/HorizontalStackedBarChartReport';
import { GridResponseTableReport } from 'components/reports/GridResponseTableReport';
import { ResponseTableReport } from 'components/reports/ResponseTableReport';
import { ResponseByCountResult } from 'model/reports/ResponseByCountResult';
import { StatisticsTableReport } from 'components/reports/StatisticsTableReport';
import { StatisticsResults } from 'model/reports/StatisticsResults';
import { ExportData } from 'model/reports/export/ExportData';
import * as ChartJs from 'chart.js';
import { ListChartData } from 'components/reports/base/ListChartData';
import { GridChartData } from 'components/reports/base/GridChartData';
import { RanksTableReport } from 'components/reports/RanksTableReport';
import { RankResults } from 'model/reports/RanksData';

export type ExportFn = () => ExportData | null;

export interface ReportBuilder {
  element: ReactElement;
  getExportData: ExportFn;
}

export const getReport = (
  qText: string,
  reportType: ReportType,
  qReports: QuestionReports
): ReportBuilder => {
  switch (reportType) {
    case ReportType.BarChart:
      return getListChartReport(
        qText,
        BarChartReport,
        qReports.listResults as ListResults
      );
    case ReportType.HorizontalBarChart:
      return getListChartReport(
        qText,
        HorizontalBarChartReport,
        qReports.listResults as ListResults
      );
    case ReportType.PieChart:
      return getListChartReport(
        qText,
        PieChartReport,
        qReports.listResults as ListResults
      );
    case ReportType.DoughnutChart:
      return getListChartReport(
        qText,
        DoughnutChartReport,
        qReports.listResults as ListResults
      );
    case ReportType.StackedBarChart:
      return getGridChartReport(
        qText,
        StackedBarChartReport,
        qReports.gridResults as GridResults
      );
    case ReportType.HorizontalStackedBarChart:
      return getGridChartReport(
        qText,
        HorizontalStackedBarChartReport,
        qReports.gridResults as GridResults
      );
    case ReportType.GridResponseTable:
      return {
        element: React.createElement(GridResponseTableReport, {
          report: qReports.gridResults as GridResults,
        }),
        getExportData: () => ({
          text: qText,
          type: 'gridTable',
          data: qReports.gridResults as GridResults,
        }),
      };
    case ReportType.ResponseTable:
      return {
        element: React.createElement(ResponseTableReport, {
          report: qReports.responseByCountResults as ResponseByCountResult,
        }),
        getExportData: () => ({
          text: qText,
          type: 'responseByCountTable',
          data: qReports.responseByCountResults as ResponseByCountResult,
        }),
      };
    case ReportType.StatisticsTable:
      return {
        element: React.createElement(StatisticsTableReport, {
          report: qReports.statisticsTableResults as StatisticsResults,
        }),
        getExportData: () => ({
          text: qText,
          type: 'statisticsTable',
          data: qReports.statisticsTableResults as StatisticsResults,
        }),
      };
    case ReportType.RanksTableReport:
      return {
        element: React.createElement(RanksTableReport, {
          report: qReports.rankListTableResults as RankResults,
        }),
        getExportData: () => ({
          text: qText,
          type: 'RanksTable',
          data: qReports.rankListTableResults as RankResults,
        }),
      };
    default:
      throw Error(
        `Unknown Report Type (${reportType}) was used in report builder.`
      );
  }
};

const getListChartReport = (
  qText: string,
  report: React.FC<ListChartData>,
  data: ListResults
): ReportBuilder => {
  let chartInstance: ChartJs | null = null;
  const el = React.createElement(report, {
    report: data,
    instance: (instance: ChartJs) => {
      chartInstance = instance;
    },
  });
  return {
    element: el,
    getExportData: () =>
      chartInstance
        ? {
            text: qText,
            type: 'image',
            data: chartInstance.toBase64Image(),
          }
        : null,
  };
};

const getGridChartReport = (
  qText: string,
  report: React.FC<GridChartData>,
  data: GridResults
): ReportBuilder => {
  let chartInstance: ChartJs | null = null;
  const el = React.createElement(report, {
    report: data,
    instance: (instance: ChartJs) => {
      chartInstance = instance;
    },
  });
  return {
    element: el,
    getExportData: () =>
      chartInstance
        ? {
            text: qText,
            type: 'image',
            data: chartInstance.toBase64Image(),
          }
        : null,
  };
};
